import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  IconButton,
  Stack,
  OutlinedInput,
} from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { useQuery } from '@tanstack/react-query';
import { teacherService } from 'src/services/teacher.service';
import { CreateCohortDTO } from 'src/types/cohort.types';

interface CreateCohortModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (dto: CreateCohortDTO) => Promise<void>;
  classes: Array<{ id: string; name: string }>;
  defaultClassId?: string;
  isSubmitting?: boolean;
}

export const CreateCohortModal: React.FC<CreateCohortModalProps> = ({
  open,
  onClose,
  onSubmit,
  classes,
  defaultClassId = '',
  isSubmitting = false,
}) => {
  const [classId, setClassId] = useState(defaultClassId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch teachers for multi-select
  const { data: teacherData } = useQuery({
    queryKey: ['teachers-all'],
    queryFn: () => teacherService.getTeachers({ page: 1, limit: 100 }),
    enabled: open,
  });

  const teachers = teacherData?.data || [];

  React.useEffect(() => {
    if (defaultClassId) setClassId(defaultClassId);
  }, [defaultClassId, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!classId) {
      setError('Please select a parent Grade / Class.');
      return;
    }
    if (!name.trim()) {
      setError('Cohort name is required (e.g. 7A, 7B).');
      return;
    }

    try {
      setError(null);
      await onSubmit({
        class_id: classId,
        name: name.trim(),
        description: description.trim() || undefined,
        teacher_ids: selectedTeacherIds,
      });
      setName('');
      setDescription('');
      setSelectedTeacherIds([]);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create cohort.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#1A202C">
            Create New Cohort
          </Typography>

          <Typography variant="caption" color="text.secondary">
            Add a new cohort (e.g. 7A, 7B) under a Grade level
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <IoClose />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ py: 2 }}>
          {error && (
            <Box sx={{ mb: 2, p: 1.5, bg: '#FFF5F5', color: '#E53E3E', borderRadius: 1, fontSize: '0.875rem' }}>
              {error}
            </Box>
          )}

          <Stack spacing={2.5}>
            <FormControl fullWidth size="small" required>
              <InputLabel id="select-class-label">Parent Grade / Class</InputLabel>
              <Select
                labelId="select-class-label"
                value={classId}
                label="Parent Grade / Class"
                onChange={(e) => setClassId(e.target.value)}
              >
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Cohort Name"
              placeholder="e.g. 7A, 7B, 7C"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              required
              helperText="Common examples: 6A, 7B, SPED-7, Honors 8"
            />

            <TextField
              label="Description (Optional)"
              placeholder="Optional notes or section details"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="select-teachers-label">Assign Teachers (Multi-Select)</InputLabel>
              <Select
                labelId="select-teachers-label"
                multiple
                value={selectedTeacherIds}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedTeacherIds(typeof val === 'string' ? val.split(',') : val);
                }}
                input={<OutlinedInput label="Assign Teachers (Multi-Select)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((teacherId) => {
                      const t = teachers.find((t) => t.id === teacherId);
                      return (
                        <Chip
                          key={teacherId}
                          label={t ? `${t.user?.firstname} ${t.user?.lastname}` : teacherId}
                          size="small"
                          sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 600 }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {teachers.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.user?.firstname} {t.user?.lastname} ({t.user?.email}) {t.subject ? `- ${t.subject}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#EDB42E',
              color: '#000',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#d9a224' },
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Cohort'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
